import { deleteJson, getJson, putJson } from '@/shared/api/apiClient';

/*
 * How the Interactive Text panel reads: how the active line is marked, the text
 * size, and the typeface.
 *
 * The typeface choice is the point of the feature rather than decoration —
 * Atkinson Hyperlegible Next is the default because it is designed for low
 * vision, and the alternatives cover dyslexia-friendly and conventional serif
 * reading. Which is exactly why this belongs on the account: someone who needs a
 * particular typeface to read comfortably should not have to find the setting
 * again on every device.
 *
 * Backed by `/api/v1/preferences/reading` — a JSONB blob, not columns. Three
 * enums today; the shape is a design decision and should not cost a migration
 * when it becomes four.
 */

const PATH = '/api/v1/preferences/reading';

export type ActiveLineStyle = 'highlight' | 'underline' | 'none';
export type ReadingTextSize = 'sm' | 'md' | 'lg';
export type ReadingTypeface = 'hyperlegible' | 'serif' | 'sans';

export type ReadingPrefs = {
  activeLine: ActiveLineStyle;
  textSize: ReadingTextSize;
  typeface: ReadingTypeface;
};

export const DEFAULT_READING_PREFS: ReadingPrefs = {
  activeLine: 'highlight',
  textSize: 'md',
  typeface: 'hyperlegible',
};

export const TYPEFACE_LABELS: Record<ReadingTypeface, string> = {
  // Names the family actually applied by `.transcript--face-hyperlegible`,
  // which resolves through `--font-ui`.
  hyperlegible: 'Atkinson Hyperlegible Next',
  serif: 'Goudy Bookletter 1911',
  sans: 'System sans',
};

/*
 * Defaults are applied here, not server-side. The server stores whatever was
 * set and returns {} for a user who has never set anything — so the defaults
 * live in exactly one place, next to the UI whose design decides them, instead
 * of being duplicated into a database that would then need a migration to
 * change a colour.
 */
export async function getReadingPrefs(_userId: string): Promise<ReadingPrefs> {
  // Spread over the defaults rather than returning the stored object: a
  // preference added after this was written is absent from an older record,
  // and the default is the right answer for it.
  return { ...DEFAULT_READING_PREFS, ...(await getJson<Partial<ReadingPrefs>>(PATH)) };
}

export async function setReadingPrefs(
  _userId: string,
  prefs: Partial<ReadingPrefs>,
): Promise<ReadingPrefs> {
  // The server shallow-merges, so sending only what changed is enough and two
  // screens writing different fields cannot clobber each other.
  const stored = await putJson<Partial<ReadingPrefs>>(PATH, prefs);
  return { ...DEFAULT_READING_PREFS, ...stored };
}

export async function resetReadingPrefs(_userId: string): Promise<ReadingPrefs> {
  await deleteJson<Record<string, never>>(PATH);
  return DEFAULT_READING_PREFS;
}
